/**
 * Compress camera-captured images before LPR (license plate recognition) upload.
 * Phone cameras often produce 2–8+ MB images; this resizes and compresses to
 * a reasonable size so uploads are fast while keeping the plate readable for OCR.
 *
 * Tuned for better recognition: 800px max dimension, 0.65 quality (was 600px / 0.55).
 */

export interface LprCompressionOptions {
  /** Max width or height in pixels (default 800). Keeps plate text readable while reducing file size. */
  maxDimension?: number;
  /** JPEG quality 0–1 (default 0.65). Balances file size with plate recognition accuracy. */
  jpegQuality?: number;
}

const DEFAULT_MAX_DIMENSION = 800;
const DEFAULT_JPEG_QUALITY = 0.65;

/**
 * Compress an image file for LPR upload: resize so longest side ≤ maxDimension,
 * encode as JPEG with given quality. Works for camera captures from Android and iOS.
 * @param file Image file (e.g. from <input type="file" accept="image/*"> or camera)
 * @param options Optional max dimension and JPEG quality
 * @returns Compressed image as a new File (JPEG), or rejects if not a valid image
 */
export function compressImageForLpr(
  file: File,
  options: LprCompressionOptions = {}
): Promise<File> {
  const maxDimension = options.maxDimension ?? DEFAULT_MAX_DIMENSION;
  const jpegQuality = options.jpegQuality ?? DEFAULT_JPEG_QUALITY;

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      try {
        URL.revokeObjectURL(url);
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        if (w === 0 || h === 0) {
          reject(new Error('Invalid image dimensions'));
          return;
        }

        let targetW = w;
        let targetH = h;
        if (w > maxDimension || h > maxDimension) {
          if (w >= h) {
            targetW = maxDimension;
            targetH = Math.round((h * maxDimension) / w);
          } else {
            targetH = maxDimension;
            targetW = Math.round((w * maxDimension) / h);
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = targetW;
        canvas.height = targetH;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas not supported'));
          return;
        }
        ctx.drawImage(img, 0, 0, targetW, targetH);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to compress image'));
              return;
            }
            const name = file.name.replace(/\.[^.]+$/i, '') || 'image';
            const compressedFile = new File([blob], `${name}.jpg`, {
              type: 'image/jpeg',
              lastModified: Date.now()
            });
            resolve(compressedFile);
          },
          'image/jpeg',
          jpegQuality
        );
      } catch (err) {
        URL.revokeObjectURL(url);
        reject(err instanceof Error ? err : new Error('Compression failed'));
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
}
